import type React from "react";
import { useEffect, useRef } from "react";
import { useAnimation } from "@/features/animation";
import { useProject } from "@/features/project";
import {
  combineCode,
  createBootstrapScript,
  createHarnessScript,
  createPreviewStyles,
} from "./construct";

export const PreviewFrame: React.FC = () => {
  const { stableSnapshot, dimensions, assetUrlMap, data } = useProject();
  const { controller } = useAnimation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const stableCode = combineCode(stableSnapshot.files);
  const stableData = stableSnapshot.data;

  const stableAssetUrlMapJson = JSON.stringify(assetUrlMap);
  const stableDataJson = JSON.stringify(stableData);
  const bootstrapScript = createBootstrapScript(stableAssetUrlMapJson, stableDataJson);
  const harnessScript = createHarnessScript(dimensions);
  const previewStyles = createPreviewStyles(dimensions);

  const STATIC_HTML = `<canvas id="mainCanvas"></canvas>`;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframe.srcdoc = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          ${previewStyles}
        </head>
        <body>
          ${bootstrapScript}
          <div id="stage">
            ${STATIC_HTML}
          </div>
          <script>
            ${stableCode}
          </script>
          ${harnessScript}
        </body>
      </html>
    `;

    // Re-attach iframe to controller after content loads
    const handleLoad = () => {
      controller.setIframe(iframe);
    };
    iframe.addEventListener("load", handleLoad);

    return () => {
      iframe.removeEventListener("load", handleLoad);
    };
  }, [stableCode, previewStyles, harnessScript, bootstrapScript, controller]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.postMessage({ type: "UPDATE_ASSET_MAP", data: { assetUrlMap } }, "*");
  }, [assetUrlMap]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.postMessage({ type: "UPDATE_DATA", data: { data } }, "*");
  }, [data]);

  return (
    <iframe
      ref={iframeRef}
      title="Preview"
      className="flex-1 w-full border-none bg-slate-900"
      sandbox="allow-scripts allow-same-origin"
    />
  );
};
